import { z } from 'zod';

// Canonical live score/status update.
// Emitted to Redis Stream 'score.event' by connectors.
// Consumed by: Live Context Worker, Decision Engine (SLO guard), Storage Writers.

const utcDatetime = z
  .string()
  .datetime({ offset: true })
  .refine((s) => s.endsWith('Z'), {
    message: 'Timestamp must be UTC (Z suffix)',
  });

export const ScoreEventSchema = z
  .object({
    sourceMatchId: z.string().min(1),
    canonicalMatchId: z.string().uuid().nullable(),
    observedAt: utcDatetime,
    source: z.enum(['api_tennis', 'oddspapi']),
    // Canonical status — mapped from source-specific values in normalize.ts
    status: z.enum([
      'scheduled',
      'live',
      'set_1',
      'set_2',
      'set_3',
      'set_4',
      'set_5',
      'tiebreak',
      'suspended',
      'completed',
      'cancelled',
      'retired',
      'walkover',
      'unknown',
    ]),
    isLive: z.boolean(),
    // Score per set: [{set: 1, p1: 3, p2: 6}, ...]
    scores: z.array(
      z.object({
        set: z.number().int().positive(),
        p1: z.number().int().nonnegative(),
        p2: z.number().int().nonnegative(),
      }),
    ),
    // Current game score: "30 - 15", "AD - 40", "0 - 0", null when not live
    gameScore: z.string().nullable(),
    serving: z.enum(['p1', 'p2']).nullable(),
    playerA: z.string().min(1),
    playerB: z.string().min(1),
    tournament: z.string().min(1),
    startsAt: utcDatetime,
  })
  .refine((e) => !(e.isLive && (e.status === 'scheduled' || e.status === 'completed')), {
    message: 'isLive=true is incompatible with status scheduled or completed',
    path: ['isLive'],
  });

export type ScoreEvent = z.infer<typeof ScoreEventSchema>;
